import _ from 'lodash';
import * as jsonpatch from 'fast-json-patch';

import { COLLECTION_TYPES } from '@/core/collections/constants';

// TODO: type this — Vuex module context and permissions object types
/* eslint-disable @typescript-eslint/no-explicit-any */
type Ctx = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

export interface PermissionSettings {
    deleteToken: boolean;
    editAnyMap: boolean;
    gridControl: boolean;
    fogControl: boolean;
    hiddenTokens: boolean;
    moveAll: boolean;
    [key: string]: boolean;
}

export const defaultPermissions: PermissionSettings = {
    deleteToken: true,
    editAnyMap: true,
    gridControl: false,
    fogControl: false,
    hiddenTokens: false,
    moveAll: false,
};

function setLocalPermissions(permissions: Partial<PermissionSettings>): PermissionSettings {
    const allPermissions = _.cloneDeep(defaultPermissions);
    Object.assign(allPermissions, permissions);
    return allPermissions;
}

interface GmPermissionsState {
    displayGmControlsModal: boolean;
}

const gmPermissionsStore = {
    namespaced: true,
    state: {
        displayGmControlsModal: false,
    } as GmPermissionsState,
    getters: {
        permissions(_state: Ctx, _getters: Ctx, _rootState: Ctx, rootGetters: Ctx) {
            return rootGetters['collections/getCollection'](COLLECTION_TYPES.permissions);
        },
        permissionsKey(_state: Ctx, getters: Ctx) {
            const onlyPermissionsItemKey = Object.keys(getters.permissions)[0];
            return onlyPermissionsItemKey;
        },
        permissionSettings(_state: Ctx, getters: Ctx) {
            const { permissions, permissionsKey } = getters;
            if (!_.isEmpty(permissions)) {
                return setLocalPermissions(permissions[permissionsKey].settings);
            } else {
                return defaultPermissions;
            }
        },
    },
    mutations: {
        toggleDisplayGmControlsModal(state: GmPermissionsState): void {
            state.displayGmControlsModal = !state.displayGmControlsModal;
        },
    },
    actions: {
        async setGmPermissionSetting({ dispatch, getters }: Ctx, toggledPermission: string): Promise<void> {
            if (!_.isEmpty(getters.permissions)) {
                dispatch('permissionsExist', toggledPermission);
            } else {
                dispatch('permissionsDoNotExist', toggledPermission);
            }
        },
        async permissionsExist({ dispatch, getters }: Ctx, permissionName: string): Promise<void> {
            const patch = await dispatch('createUpdatePatch', permissionName);
            if (patch.length > 0) {
                dispatch(
                    'collections/update',
                    {
                        collection: COLLECTION_TYPES.permissions,
                        id: getters.permissionsKey,
                        patch,
                    },
                    { root: true },
                );
            }
        },
        async permissionsDoNotExist({ dispatch, getters }: Ctx, permissionName: string): Promise<void> {
            const setting = { [permissionName]: !getters.permissionSettings[permissionName] };
            const permissions = { settings: setting };
            dispatch(
                'collections/add',
                { collection: COLLECTION_TYPES.permissions, item: permissions },
                { root: true },
            );
        },
        createComparableCollections({ getters }: Ctx): Ctx {
            const currentCollection = getters.permissions[getters.permissionsKey];
            const editableCollection = _.cloneDeep(currentCollection);
            return { editableCollection, currentCollection };
        },
        async createUpdatePatch({ dispatch, getters }: Ctx, permissionName: string): Promise<Ctx> {
            const { editableCollection, currentCollection } = await dispatch('createComparableCollections');
            Object.assign(editableCollection.settings, {
                [permissionName]: !getters.permissionSettings[permissionName],
            });
            return jsonpatch.compare(currentCollection, editableCollection);
        },
        async loadGmPermissions({ dispatch }: Ctx): Promise<unknown> {
            return await dispatch('collections/load', { collection: COLLECTION_TYPES.permissions }, { root: true });
        },
    },
};

export default gmPermissionsStore;
